import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { Button } from '@material-ui/core';
import clsx from 'clsx';
import AdminPage from '../AdminPage';
import { buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import { AGENT_NAME_FIELD, AGENTS_FUNCTION_NAME } from './AgentConstants';
import { useLatestAgentData } from './AgentDetailHelper';
import AgentReadOnlySecondary from './AgentReadOnlySecondary';
import AgentHealthTrends from './AgentHealthTrends';
import Actions from '../../page-framework/actions/Actions';
import Link from '../../links/Link';
import { useStyles as useFormStyles } from '../../form/FormStyles';
import { useStyles as useActionStyles } from '../../page-framework/actions/ActionStyles';
import ReportAction from '../../page-framework/actions/ReportAction';
import { TIME_RANGE_8_HOURS_OPTION } from './AgentHealthHelper';
import useIsPrinting from '../../../utilities/PrintUtilities';
import NgpFieldHelpTip from '../../form/NgpFieldHelpTip';

const functionName = AGENTS_FUNCTION_NAME;
const listUrl = `/admin/${functionName}`;
const detailUrl = '../';
const listBreadCrumbText = i18n._(t`nPoints`);
const healthBreadCrumbText = i18n._(t`Health`);
const backButtonText = i18n._(t`Back`);
const pageTitle = t`nPoint Health`;
const timeQueryString = 'time';
const logButtonText = i18n._(t`Download nPoint log`);
const helpTipText = t`You can only download log file when the nPoint is running and has a version more recent than than 3.9.0-0.0.0`;

export default function AgentAdminDetailHealth() {
    const classes = useFormStyles();
    const actionClasses = useActionStyles();
    const recordId = useEntityIdParam();

    const {
        latestAgentData,
        isHardware3kAgent,
        isHardwareAgent,
        isWirelessAgent,
        isHardwareEdgeSensorAgent,
        allowCollectLogs,
        isGlobalAgent,
    } = useLatestAgentData(recordId);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const { search } = useLocation();
    const isPrinting = useIsPrinting();
    const queryString = new URLSearchParams(search);
    const startingTimeRange = queryString.get(timeQueryString) || TIME_RANGE_8_HOURS_OPTION;
    const [timeRange, setTimeRange] = useState(startingTimeRange);
    const logUrl = `/api/v3/npoints/${recordId}/getlogs`;

    useEffect(() => {
        setBreadcrumbs([
            { link: listUrl, text: listBreadCrumbText },
            { link: detailUrl, text: _.get(latestAgentData, AGENT_NAME_FIELD) },
            { text: healthBreadCrumbText },
        ]);
        // can't watch and set breadCrumbsArray
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId, latestAgentData]);

    return (
        <AdminPage
            title={t`nPoint Health`}
            pageId={buildPageId(AGENTS_FUNCTION_NAME)}
            breadcrumbs={breadcrumbs}
            actions={
                <Actions>
                    <Link to={detailUrl}>
                        <Button>{backButtonText}</Button>
                    </Link>
                    <ReportAction
                        pageTitle={pageTitle}
                        options={{
                            canSchedule: false,
                            pageUrlPath: `/admin/nPoints/${recordId}/metrics?${timeQueryString}=${timeRange}`,
                        }}
                    />
                </Actions>
            }
        >
            <div className="scroll-container">
                {latestAgentData && (
                    <AgentHealthTrends
                        agent={latestAgentData}
                        isHardware3kAgent={isHardware3kAgent}
                        isHardwareAgent={isHardwareAgent}
                        isGlobalAgent={isGlobalAgent}
                        timeRange={timeRange}
                        setTimeRange={setTimeRange}
                    />
                )}

                <AgentReadOnlySecondary
                    agent={latestAgentData}
                    isHardware3kAgent={isHardware3kAgent}
                    isHardwareAgent={isHardwareAgent}
                    isWirelessAgent={isWirelessAgent}
                    isHardwareEdgeSensorAgent={isHardwareEdgeSensorAgent}
                    isGlobalAgent={isGlobalAgent}
                    showExtendedInformation
                />

                <span className={clsx(actionClasses.actions)} style={{ justifyContent: 'flex-start' }}>
                    <Link to={logUrl} style={{ pointerEvents: allowCollectLogs.current ? '' : 'none' }}>
                        <Button disabled={!allowCollectLogs.current}>{logButtonText}</Button>
                    </Link>
                    <NgpFieldHelpTip helpText={helpTipText} />
                </span>

                {!isPrinting && (
                    <div className={classes.ngpBottomActions}>
                        <Link to={detailUrl}>
                            <Button variant="contained" color="primary">
                                {backButtonText}
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </AdminPage>
    );
}
